import { Step as ModelStep } from "@redotech/redo-model/return-flow";
import { exchangeShippingNameFromModel } from "@redotech/redo-model/return-flow-step-from-model";
import {
  ExchangeShippingNameState,
  isExchangeShippingNameStepValid,
} from "@redotech/redo-model/return-flow-step-valid";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import TagIcon from "@redotech/redo-web/icon-old/tag.svg";
import { produce } from "immer";
import * as React from "react";
import { StepDownstream, StepType, StepTypeDetailsProps } from "../step";

export const EXCHANGE_SHIPPING_NAME = (
  Details: React.ComponentType<StepTypeDetailsProps<ExchangeShippingNameState>>,
): StepType<ExchangeShippingNameState, ModelStep.ExchangeShippingName> => ({
  Details: (props: StepTypeDetailsProps<ExchangeShippingNameState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: "Shipping name",
  valid(state) {
    return isExchangeShippingNameStepValid(state).valid;
  },
  Icon: TagIcon,
  description(state) {
    return state.shippingName;
  },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  empty: { next: undefined, shippingName: "" },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(
        `Invalid handle index ${handleIndex} for Exchange Shipping Name step`,
      );
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  layout() {
    return BlockLayout.FULL;
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.EXCHANGE_SHIPPING_NAME,
      next: state.next ? id(state.next) : undefined,
      shippingName: state.shippingName,
    };
  },
  fromModel(model, id) {
    return exchangeShippingNameFromModel(model, id);
  },
});

export { type ExchangeShippingNameState } from "@redotech/redo-model/return-flow-step-valid";
